/**
 * stateAt (03 §3): per-car race state at an arbitrary race time, reproducing the reference
 * `common.decoded_state_at` (and so v6 `state_at`) over the decoded Timeline.
 *
 * - clamp t to [0, (F−1)/hz]; f = t·hz, i = min(floor(f), F−2), u = f − i
 * - positions: Catmull-Rom (v6 formula) through the frame positions P_j = ref(dist_j mod lap) + nrm·lat_j,
 *   j = i−1 … i+2 clamped to [0, F−1]
 * - continuous fields linear in f64; gap/int: a NaN side takes the other side; s = dist mod lap
 * - hdg: h_i + u·wrapπ(h_{i+1} − h_i), wrapped to [0, 2π)
 * - discrete fields held from frame i (i+1 when u > 1 − 1e−9); order / leader from pos
 *
 * Director parity rule (03 §10): pass `holdT` (the clock's `frameRaceTime`) to read the discrete fields
 * at the v6 frame index while positions stay smooth. Allocation-free.
 */
import { FLAG, type FrameState, type Timeline } from '../api.ts';
import { pyMod } from '../bundle/timeline.ts';
import type { TrackLut } from './track.ts';

const TWO_PI = 2 * Math.PI;

export interface StateOptions {
  /** race time the discrete fields (pos, order, lap, flags, …) are held at; default t */
  holdT?: number;
  /** use the modelled lateral offsets (tl.latModel) when present (08) */
  latModel?: boolean;
}

export function createFrameState(nCars: number): FrameState {
  return {
    t: 0, lap: 0, leader: 0, order: new Uint8Array(nCars),
    x: new Float32Array(nCars), y: new Float32Array(nCars), hdg: new Float32Array(nCars), s: new Float32Array(nCars),
    dist: new Float64Array(nCars), lat: new Float32Array(nCars),
    speed: new Float64Array(nCars), thr: new Float64Array(nCars), rpm: new Float64Array(nCars),
    gear: new Uint8Array(nCars), flags: new Uint16Array(nCars), pos: new Uint8Array(nCars),
    gap: new Float64Array(nCars), int: new Float64Array(nCars), lapsdn: new Uint8Array(nCars), lapNo: new Uint8Array(nCars),
    cmp: new Uint8Array(nCars), tyre: new Uint8Array(nCars), stint: new Uint8Array(nCars), pits: new Uint8Array(nCars),
    latSource: 'data',
  };
}

/** f = t / dt exactly as Python (`float(t) / DT` with DT = 0.1; t·10 is not bit-identical), clamped */
export function frameOf(tl: Timeline, t: number): number {
  const tt = Number.isNaN(t) ? 0 : t;
  return Math.min(Math.max(tt / (1 / tl.hz), 0), tl.frames - 1);
}

/** Uniform Catmull-Rom between p1 and p2 (v6 _catmull, same operation order). */
export function catmull(p0: number, p1: number, p2: number, p3: number, u: number): number {
  const u2 = u * u, u3 = u * u * u;
  return 0.5 * ((2 * p1) + (-p0 + p2) * u + (2 * p0 - 5 * p1 + 4 * p2 - p3) * u2
    + (-p0 + 3 * p1 - 3 * p2 + p3) * u3);
}

const P = new Float64Array(8);        // scratch: 4 frame positions (x, y)

/** frame position of car c at frame j: ref(dist mod lap) + nrm · lat (into P[o], P[o+1]) */
function framePos(tl: Timeline, lat: Float32Array, track: TrackLut, j: number, c: number, o: number): void {
  const k = j * tl.nCars + c;
  track.place(pyMod(tl.dist[k] ?? 0, tl.lapM), lat[k] ?? 0, P, o);
}

function lerpHoldNaN(a0: number, a1: number, u: number): number {
  if (Number.isNaN(a1)) return a0;
  if (Number.isNaN(a0)) return a1;
  return a0 + u * (a1 - a0);
}

export function stateAt(tl: Timeline, track: TrackLut, t: number, out: FrameState, opts?: StateOptions): FrameState {
  const F = tl.frames, C = tl.nCars;
  const f = frameOf(tl, t);
  const i = Math.min(Math.floor(f), F - 2);
  const u = f - i;
  const ia = Math.max(i - 1, 0), ib = i, ic = i + 1, id = Math.min(i + 2, F - 1);
  const useModel = opts?.latModel === true && tl.latModel !== undefined;
  const lat = useModel && tl.latModel ? tl.latModel : tl.lat;
  out.latSource = useModel ? 'model' : 'data';
  out.t = f * (1 / tl.hz);                           // Python: f * DT
  const b = ib * C, n1 = ic * C;
  for (let c = 0; c < C; c++) {
    framePos(tl, lat, track, ia, c, 0);
    framePos(tl, lat, track, ib, c, 2);
    framePos(tl, lat, track, ic, c, 4);
    framePos(tl, lat, track, id, c, 6);
    out.x[c] = catmull(P[0] ?? 0, P[2] ?? 0, P[4] ?? 0, P[6] ?? 0, u);
    out.y[c] = catmull(P[1] ?? 0, P[3] ?? 0, P[5] ?? 0, P[7] ?? 0, u);

    const d0 = tl.dist[b + c] ?? 0, d1 = tl.dist[n1 + c] ?? 0;
    const d = d0 + u * (d1 - d0);
    out.dist[c] = d;
    out.s[c] = pyMod(d, tl.lapM);
    const l0 = lat[b + c] ?? 0, l1 = lat[n1 + c] ?? 0;
    out.lat[c] = l0 + u * (l1 - l0);
    const s0 = tl.speed[b + c] ?? 0, s1 = tl.speed[n1 + c] ?? 0;
    out.speed[c] = s0 + u * (s1 - s0);
    const t0 = tl.thr[b + c] ?? 0, t1 = tl.thr[n1 + c] ?? 0;
    out.thr[c] = t0 + u * (t1 - t0);
    const r0 = tl.rpm[b + c] ?? 0, r1 = tl.rpm[n1 + c] ?? 0;
    out.rpm[c] = r0 + u * (r1 - r0);
    out.gap[c] = lerpHoldNaN(tl.gap[b + c] ?? NaN, tl.gap[n1 + c] ?? NaN, u);
    out.int[c] = lerpHoldNaN(tl.int[b + c] ?? NaN, tl.int[n1 + c] ?? NaN, u);
    const h0 = tl.hdg[b + c] ?? 0, h1 = tl.hdg[n1 + c] ?? 0;
    out.hdg[c] = pyMod(h0 + u * (pyMod(h1 - h0 + Math.PI, TWO_PI) - Math.PI), TWO_PI);
  }
  holdDiscrete(tl, opts?.holdT ?? null, ib, ic, u, out);
  return out;
}

function holdDiscrete(tl: Timeline, holdT: number | null, ib: number, ic: number, u: number, out: FrameState): void {
  const C = tl.nCars;
  let j: number;
  if (holdT === null) {
    j = u > 1 - 1e-9 ? ic : ib;
  } else {
    const fh = frameOf(tl, holdT);
    const ih = Math.min(Math.floor(fh), tl.frames - 2);
    j = fh - ih > 1 - 1e-9 ? ih + 1 : ih;
  }
  const o = j * C;
  for (let c = 0; c < C; c++) {                      // index loops: no subarray views (allocation-free)
    const k = o + c;
    out.gear[c] = tl.gear[k] ?? 0;
    out.flags[c] = tl.flags[k] ?? 0;
    out.pos[c] = tl.pos[k] ?? 0;
    out.lapsdn[c] = tl.lapsdn[k] ?? 0;
    out.lapNo[c] = tl.lap[k] ?? 0;
    out.cmp[c] = tl.cmp[k] ?? 0;
    out.tyre[c] = tl.tyre[k] ?? 0;
    out.stint[c] = tl.stint[k] ?? 0;
    out.pits[c] = tl.pits[k] ?? 0;
  }
  orderFromPos(out.pos, out.order);
  out.leader = out.order[0] ?? 0;
  out.lap = out.lapNo[out.leader] ?? 0;
}

/** order[p − 1] = car with position p (cars without a valid position fill the remaining slots by index) */
export function orderFromPos(pos: Uint8Array, order: Uint8Array): void {
  const C = pos.length;
  order.fill(255);
  for (let c = 0; c < C; c++) {
    const p = pos[c] ?? 0;
    if (p >= 1 && p <= C && order[p - 1] === 255) order[p - 1] = c;
  }
  // defensive: duplicates / zeros (never in the Monza data) keep every car exactly once
  let slot = 0;
  for (let c = 0; c < C; c++) {
    const p = pos[c] ?? 0;
    if (p >= 1 && p <= C && order[p - 1] === c) continue;
    while (slot < C && order[slot] !== 255) slot++;
    if (slot < C) order[slot] = c;
  }
}

// ───────────────────────────── per-car sampling (trails, sub-frame samples) ─────────────────────────────

export interface CarSample {
  x: number; y: number; hdg: number; s: number; dist: number; lat: number;
  speed: number; thr: number; flags: number; brake: boolean;
}

export function createCarSample(): CarSample {
  return { x: 0, y: 0, hdg: 0, s: 0, dist: 0, lat: 0, speed: 0, thr: 0, flags: 0, brake: false };
}

export interface SampleOptions {
  /** 'catmull' (default, identical to stateAt / v6 trails) or 'linear' (lerp dist and lat, then place: cheaper) */
  interp?: 'catmull' | 'linear';
  latModel?: boolean;
}

/** One car at race time t (same clamping, interpolation and hold rule as stateAt). Allocation-free. */
export function sampleCar(tl: Timeline, track: TrackLut, car: number, t: number, out: CarSample, opts?: SampleOptions): CarSample {
  const F = tl.frames, C = tl.nCars;
  const f = frameOf(tl, t);
  const i = Math.min(Math.floor(f), F - 2);
  const u = f - i;
  const lat = opts?.latModel === true && tl.latModel ? tl.latModel : tl.lat;
  const kb = i * C + car, kc = kb + C;
  const d0 = tl.dist[kb] ?? 0, d1 = tl.dist[kc] ?? 0;
  const d = d0 + u * (d1 - d0);
  const l0 = lat[kb] ?? 0, l1 = lat[kc] ?? 0;
  const l = l0 + u * (l1 - l0);
  out.dist = d;
  out.s = pyMod(d, tl.lapM);
  out.lat = l;
  if (opts?.interp === 'linear') {
    track.place(out.s, l, P, 0);
    out.x = P[0] ?? 0;
    out.y = P[1] ?? 0;
  } else {
    framePos(tl, lat, track, Math.max(i - 1, 0), car, 0);
    framePos(tl, lat, track, i, car, 2);
    framePos(tl, lat, track, i + 1, car, 4);
    framePos(tl, lat, track, Math.min(i + 2, F - 1), car, 6);
    out.x = catmull(P[0] ?? 0, P[2] ?? 0, P[4] ?? 0, P[6] ?? 0, u);
    out.y = catmull(P[1] ?? 0, P[3] ?? 0, P[5] ?? 0, P[7] ?? 0, u);
  }
  const h0 = tl.hdg[kb] ?? 0, h1 = tl.hdg[kc] ?? 0;
  out.hdg = pyMod(h0 + u * (pyMod(h1 - h0 + Math.PI, TWO_PI) - Math.PI), TWO_PI);
  const s0 = tl.speed[kb] ?? 0, s1 = tl.speed[kc] ?? 0;
  out.speed = s0 + u * (s1 - s0);
  const t0 = tl.thr[kb] ?? 0, t1 = tl.thr[kc] ?? 0;
  out.thr = t0 + u * (t1 - t0);
  const flags = tl.flags[u > 1 - 1e-9 ? kc : kb] ?? 0;
  out.flags = flags;
  out.brake = (flags & FLAG.BRAKE) !== 0;
  return out;
}

// ───────────────────────────── flag helpers ─────────────────────────────

export const brake = (flags: number): boolean => (flags & FLAG.BRAKE) !== 0;
export const drs = (flags: number): boolean => (flags & FLAG.DRS) !== 0;
export const drsOpen = (flags: number): boolean => (flags & FLAG.DRS_OPEN) !== 0;
export const inPit = (flags: number): boolean => (flags & FLAG.IN_PIT) !== 0;
export const retired = (flags: number): boolean => (flags & FLAG.RETIRED) !== 0;
export const finished = (flags: number): boolean => (flags & FLAG.FINISHED) !== 0;
export const lapped = (flags: number): boolean => (flags & FLAG.LAPPED) !== 0;
/** reconstructed races only: retired, place unknown (never drawn); bit 7, never set for FastF1 */
export const hidden = (flags: number): boolean => (flags & FLAG.HIDDEN) !== 0;
/** not on the map: retired or hidden */
export const gone = (flags: number): boolean => (flags & (FLAG.RETIRED | FLAG.HIDDEN)) !== 0;
/** flag bit set for car c in a FrameState */
export const carFlag = (fs: FrameState, c: number, bit: number): boolean => ((fs.flags[c] ?? 0) & bit) !== 0;
